import { toast } from 'react-toastify';
import { ChatMessage } from '../../types';
import { checkResponse, fetchWithAuth } from '../../util';
import { AppThunk } from '../store';
import {
  AssistantPromptDto,
  EvaluateEmotionReqDto,
  EvaluateEmotionResDto,
  EvaluateRiskReqDto,
  EvaluateRiskResDto,
  TrafficLightPromptDto,
} from './assistantDto';
import {
  addEmotionsOfMessage,
  addRiskOfChat,
  addRiskOfMessage,
} from './assistantSlice';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const TRAFFIC_LIGHT_PROMPT_URL = new URL(
  'analytics/traffic-light/prompt',
  BASE_URL
);
const LIST_ASSISTANT_PROMPTS_URL = new URL('assistant/prompt', BASE_URL);
const TRAFFIC_LIGHT_URL = new URL('analytics/traffic-light', BASE_URL);
const EVALUATE_EMOTION_URL = new URL('experimental/emotion', BASE_URL);

export const getTrafficLightPrompt = async () => {
  const res = await checkResponse(fetchWithAuth(TRAFFIC_LIGHT_PROMPT_URL), {
    toastOnApiError: true,
    toastOnFetchError: true,
  });
  if (res) {
    const body: TrafficLightPromptDto = await res.json();
    return body;
  }
};

export const getAssistantPrompts = async () => {
  const res = await checkResponse(fetchWithAuth(LIST_ASSISTANT_PROMPTS_URL), {
    toastOnApiError: true,
    toastOnFetchError: true,
  });
  if (res) {
    const body: AssistantPromptDto[] = await res.json();
    return body;
  }
};

export const evaluateRiskOfChat =
  (
    chatId: string,
    messages: ChatMessage[],
    reasoning: boolean | undefined
  ): AppThunk =>
  async (dispatch) => {
    let payload: string[] = [];
    for (const message of messages) {
      payload.push(
        message.role === 'assistant' ? '<<CLIENT>>' : '<<VOLUNTEER>>'
      );
      payload.push(message.content.trim());
    }
    if (payload.length === 0) {
      toast.warn('No messages to evaluate on.');
      return;
    }

    const assistantCount = messages.reduce(
      (count, m) => count + (m.role === 'assistant' ? 1 : 0),
      0
    );

    const lastMessage = messages[messages.length - 1].content;

    dispatch(
      addRiskOfChat({
        key: chatId,
        clientMessages: assistantCount,
      })
    );

    const data: EvaluateRiskReqDto = {
      message: payload.join('\n\n'),
      reasoning: reasoning,
    };
    const res = await checkResponse(
      fetchWithAuth(TRAFFIC_LIGHT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
      { toastOnApiError: true, toastOnFetchError: true }
    );
    if (res) {
      const body: EvaluateRiskResDto = await res.json();
      dispatch(
        addRiskOfChat({
          key: chatId,
          risk: body.risk,
          reasoning_trace: body.reasoning_trace,
          clientMessages: assistantCount,
        })
      );

      dispatch(
        addRiskOfMessage({
          key: lastMessage,
          risk: body.risk,
          reasoning_trace: body.reasoning_trace,
          riskProcessing: true,
        })
      );
    }
  };

export const evaluateRiskOfMessage =
  (message: string): AppThunk =>
  async (dispatch) => {
    const data: EvaluateRiskReqDto = { message };
    dispatch(addRiskOfMessage({ key: data.message, riskProcessing: true }));
    const res = await checkResponse(
      fetchWithAuth(TRAFFIC_LIGHT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
      { toastOnApiError: true, toastOnFetchError: true }
    );
    if (res) {
      const body: EvaluateRiskResDto = await res.json();
      dispatch(
        addRiskOfMessage({
          key: data.message,
          risk: body.risk,
          riskProcessing: true,
        })
      );
    }
  };

export const evaluateEmotionsOfMessage =
  (message: string, messageId: string): AppThunk =>
  async (dispatch) => {
    const data: EvaluateEmotionReqDto = { text: message };
    const res = await checkResponse(
      fetchWithAuth(EVALUATE_EMOTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
      { toastOnApiError: true, toastOnFetchError: true }
    );
    if (res) {
      const body: EvaluateEmotionResDto = await res.json();
      dispatch(addEmotionsOfMessage({ messageId, scores: body.scores }));
    }
  };
